import { AdjustmentDeliveryNoteModelType } from '@teinor/erp/types/company/order/adjustmentDeliveryNote'
import { Fragment, useMemo, useState, useCallback } from 'react'
import ITHeaderText from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps, InputTableLeftColumnChange } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { OrderHeaderURLs, SetCompleteDataTy } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { CustomSelectWithSearchBar } from '../../../../../../../../forms/CustomSelect'
import { CustomSelectBlockedItemsSearchbar } from '../../../../../../../../forms/CustomSelect/CustomSelectBlockedItems'
import { FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { InputWithLabelMargin } from '../../../../../../../../forms/Input'
import ModalEditOrderWorkingCenter from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalEditOrderWorkingCenter'
import { parseAddressToOneLine } from '../../../../../../../../utils/globals/address'
import { TwinDictionary, dictionaryComplexFromJsonArr } from '../../../../../../../../utils/globals/dictionary'
import { sortEmployeesByUser } from '../../../../../../../../utils/globals/employees'
import { notHaveCountersWorkingCenter } from '../../../../../../../../utils/globals/order/order'
import { returnClassOfSpanTemplate } from '../../../../../../../../utils/globals/tailwind'
import { UseEditDeleteModalProps } from '../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { EmployeesPayload } from '../../../../../../../../utils/reducers/company/employees'
import { WorkingCenterPayload } from '../../../../../../../../utils/reducers/company/workingCenters'
import { getActiveCurrenciesDictionary } from '../../../../../../../../utils/reducers/getters'

type AdjustmentStepHeaderMainProps = InputTableLeftTabsProps & AdjustmentStepHeaderMainLogicProps & {
    employees: EmployeesPayload
    orderHeaderURLs: OrderHeaderURLs
}

const AdjustmentStepHeaderMain: React.FC<AdjustmentStepHeaderMainProps> = ({ employees, workingCenters, getCompleteData, handleChange, headerData, ...logic }) => {
    const { t } = useTwinTranslation()
    const { activeCurrencies, changeCurrencyId, dictWarehouses, workingCenterAddress, openModal, setOpenModal, memorizedWorkingCenters, changeWorkingCenterId } = useAdjustmentStepHeaderMainLogic({ headerData, workingCenters, handleChange, ...logic })
    const sections: FormRender<AdjustmentDeliveryNoteModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData.custom_name} className={returnClassOfSpanTemplate(5)} />
                    }
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('representative', 'Representante'),
                    items: employees || {},
                    onChange: getCompleteData,
                    className: returnClassOfSpanTemplate(5),
                    sortItems: true,
                    fieldName: 'fullname_short',
                    sortFunction: (emp) => sortEmployeesByUser(emp, false)
                },
                {
                    name: 'processed_date',
                    component: 'InputCalendarStateFull',
                    label: t('date', 'Fecha'),
                    onlyValids: true,
                    className: returnClassOfSpanTemplate(3),
                    required: true
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('draftN', 'Nº de borrador')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA') + ' ' + returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('billedBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} className={returnClassOfSpanTemplate(3)} />,
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} className={returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <CustomSelectBlockedItemsSearchbar label={t('requestedBy', 'Pedido por')} items={extraProps.memorizedWorkingCenters} value={extraProps.WorkingCenterId} onChange={extraProps.changeWorkingCenterId} className={returnClassOfSpanTemplate(3)} isStateLess={true} blockItemsFnc={extraProps.notHaveCountersWorkingCenter} sortItems={true} />
                    },
                    extraProps: {
                        memorizedWorkingCenters,
                        WorkingCenterId: headerData.WorkingCenterId,
                        changeWorkingCenterId,
                        notHaveCountersWorkingCenter
                    }
                },
                {
                    name: 'WarehouseId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('destinationWarehouse', 'Almacén destino'),
                    items: dictWarehouses,
                    className: returnClassOfSpanTemplate(3),
                    sortItems: true
                },
                {
                    name: 'BillingAddressId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <ITHeaderText label={t('workingCenterFiscalAddress', 'Dirección Fiscal centro de trabajo')} value={extraProps.workingCenterAddress} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: { workingCenterAddress }
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <CustomSelectWithSearchBar label={t('currency', 'Moneda')} items={extraProps.activeCurrencies} value={extraProps.CurrencyId} onChange={extraProps.changeCurrencyId} className={returnClassOfSpanTemplate(2)} isStateLess={true} />
                    },
                    extraProps: {
                        activeCurrencies,
                        CurrencyId: headerData.CurrencyId,
                        changeCurrencyId
                    }
                },
            ]
        }
    ]
    return (
        <Fragment>
            <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
            {openModal?.type === 'edit' && openModal?.allRowData?.WorkingCenterId ?
                <ModalEditOrderWorkingCenter id={headerData?.id} {...{ updateWorkingCenterURL: logic.orderHeaderURLs?.updateWorkingCenter, onSubmit: getCompleteData, setOpened: () => setOpenModal(null), allRowData: openModal?.allRowData }} />
                : null
            }
        </Fragment>
    )
}

interface AdjustmentStepHeaderMainLogicProps {
    headerData: TwinDictionary
    workingCenters: WorkingCenterPayload
    setCompleteData: SetCompleteDataTy
    handleChange: InputTableLeftColumnChange
}

export const useAdjustmentStepHeaderMainLogic = ({ workingCenters, headerData, setCompleteData, handleChange }: AdjustmentStepHeaderMainLogicProps) => {
    const WorkingCenterId = headerData.WorkingCenterId
    const CurrencyId = headerData.CurrencyId
    const activeCurrencies = getActiveCurrenciesDictionary()
    const dictWarehouses = useMemo(() => dictionaryComplexFromJsonArr(workingCenters?.[parseInt(WorkingCenterId || '1')]?.Warehouses || []), [workingCenters, WorkingCenterId])
    const WorkingCenter = headerData.BillingWorkingCenter
    const workingCenterAddress = useMemo(() => parseAddressToOneLine(WorkingCenter), [WorkingCenter])
    const [openModal, setOpenModal] = useState<UseEditDeleteModalProps>(null)
    const memorizedWorkingCenters = useMemo(() => {
        const copyWorkingCenters = JSON.parse(JSON.stringify(workingCenters))
        if (headerData.WorkingCenterId && headerData.WorkingCenter && !copyWorkingCenters[headerData.WorkingCenterId]) {
            copyWorkingCenters[headerData.WorkingCenterId] = { id: headerData.WorkingCenterId, name: headerData.WorkingCenter?.name }
        }
        return copyWorkingCenters
    }, [headerData?.WorkingCenterId, headerData?.WorkingCenter, workingCenters])

    const changeCurrencyId = useCallback((value: string) => {
        if (value !== CurrencyId) {
            setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const copyOld = JSON.parse(JSON.stringify(old))
                copyOld.header.CurrencyId = value
                return { ...copyOld }
            })
            handleChange(headerData?.id, 'CurrencyId', value)
        }
    }, [CurrencyId, handleChange, headerData?.id, setCompleteData])

    const changeWorkingCenterId = useCallback((value: string) => {
        if (value !== CurrencyId) {
            setOpenModal({ type: 'edit', allRowData: { WorkingCenterId: value } })
        }
    }, [setOpenModal, CurrencyId])

    return { activeCurrencies, changeCurrencyId, dictWarehouses, workingCenterAddress, openModal, setOpenModal, memorizedWorkingCenters, changeWorkingCenterId }
}



export default AdjustmentStepHeaderMain